<template>
    <AppPageFrame showHeader login="require">
        <LoadingPage v-if="!form || !moduleState || !draftId" />

        <div v-else-if="availableApplicationTypes.length == 0" class="formPage">
            <div class="notification bulmaNotification is-warning mt-4">
                <p>There are no applications available for you to start</p>
            </div>

            <div class="is-flex is-flex-direction-column is-align-items-center">
                <SaveButton large label="Return To Home Page" @click="$router.push('/')"/>
            </div>
        </div>

        <div v-else class="formPage">

            <!-- Header section -->
            <div v-if="currentSection">
                <h1>{{ currentSection?.name }}</h1>
                <progress v-if="currentSection != null" class="progress is-primary mt-5" :value="5 + currentSection.progress * 90" max="100"></progress>
            </div>
            
            <!-- Page: Select Application Types -->
            <div v-if="form.state.activePage == 'selectApplicationTypes'">
                <h2 class="pageHeading">Which types of identification would you like to apply for?</h2>

                <Columns>
                    <div class="column">
                        <FormCheckboxField 
                            v-if="isModuleEnabled('BIRTH_RECORDS')" :isDisabled="!availableApplicationTypes.includes('birthRecords')"
                            :boxLabel="availableApplicationTypes.includes('birthRecords') ? 'Birth Records' : 'Birth Records (already applied)'"
                            v-model="moduleState.miscState.enabledApplications.birthRecords"/>

                        <FormCheckboxField
                            v-if="isModuleEnabled('SOCIAL_SECURITY') && !isModuleEnabled(Feature.Type.ONLINE_SOCIAL_SECURITY_APPLICATIONS)" :isDisabled="!availableApplicationTypes.includes('socialSecurity')"
                            :boxLabel="availableApplicationTypes.includes('socialSecurity') ? 'Social Security' : 'Social Security (already applied)'"
                            v-model="moduleState.miscState.enabledApplications.socialSecurity"/>

                    </div>
                </Columns>
            </div>

            <!-- Page: Basic Citizen Info -->
            <div v-if="form.state.activePage == 'basicCitizenInfo'">
                <h2 class="pageHeading">Please provide some basic information about yourself</h2>

                <Columns>
                    <FormTextField label="First Name *" v-model="moduleState.application.firstName" trimmed autoFocus/>
                    <FormTextField label="Middle Name" v-model="moduleState.application.middleName" trimmed/>
                    <FormTextField label="Last Name *" v-model="moduleState.application.lastName" trimmed/>
                </Columns>
                
                <HalfColumn v-if="moduleState.application.socialSecurity">
                    <FormSelectField label="Your Sex / Gender *" :options="ProfileTypes.Sex" allowNullSelection v-model="moduleState.application.socialSecurity.sex"/>
                </HalfColumn>

                <Columns>
                    <FormDateField label="Date Of Birth *" v-model="moduleState.application.dateOfBirth" past/>
                    
                    <div class="column" style="padding-top: 2.75rem">
                        <span v-if="moduleState.application.dateOfBirth">
                            You are <strong>{{ formatAge(moduleState.application.dateOfBirth) }}</strong> old
                        </span>
                    </div>
                </Columns>
            </div>

            <!-- Page: Birthplace -->
            <div v-if="form.state.activePage == 'birthPlace'">
                <h2 class="pageHeading">Please provide some information about where you were born</h2>

                <Columns>
                    <FormTextField label="Birth City *" v-model="moduleState.application.birthCity" trimmed autoFocus/>
                    <FormSelectField label="Birth State *" :options="ProfileTypes.StateNotUnknown" allowNullSelection v-model="moduleState.application.birthState"/>
                </Columns>

                <div v-if="moduleState.application.birthRecords">
                    <Columns>
                        <FormTextField label="Birth Hospital *" v-model="moduleState.application.birthRecords.birthHospital" trimmed/>
                        <FormTextField label="Birth County" v-model="moduleState.application.birthRecords.birthCounty" trimmed/>
                    </Columns>

                    <FormSelectField v-if="moduleState.application.birthState! in districts" label="Birth District *" :options="ProfileTypes.BirthDistrict" :includedOptions="districts[moduleState.application.birthState!]" allowNullSelection v-model="moduleState.application.birthRecords.birthDistrict"/>
                </div>
            </div>

            <!-- Page: Social Security -->
            <div v-if="form.state.activePage == 'socialSecurity'">
                <h2 class="pageHeading">Please provide some information about your Social Security status</h2>

                <div v-if="moduleState.application.socialSecurity">
                    <Columns>
                        <FormRadioGroupField label="Have you ever applied for a Social Security card before? *" :options="ProfileTypes.YesNoUnknown" v-model="moduleState.application.socialSecurity.previouslyAppliedForCard" />
                        <FormTextField label="Your Social Security Number (if known)" v-model="moduleState.application.socialSecurity.previousSocialSecurityNumber" trimmed :length="9" isPassword noAutoFill/>
                    </Columns>

                    <HalfColumn>
                        <FormRadioGroupField label="What is your citizenship status? *" :options="SocialSecurity.Citizenship" v-model="moduleState.application.socialSecurity.citizenship" stacked/>
                    </HalfColumn>
                </div>
            </div>

            <!-- Page: Parents -->
            <div v-if="form.state.activePage == 'parents'">
                <h2 class="pageHeading">Please provide some information about your mother</h2>

                <Columns>
                    <FormTextField label="Birth Mother's Full Name" v-model="moduleState.application.motherName" trimmed autoFocus/>
                    <FormTextField label="Mother's Surname Before Marriage" v-model="moduleState.application.motherMaidenName" trimmed/>
                </Columns>

                <div v-if="moduleState.application.birthRecords">
                    <Columns>
                        <FormTextField label="Mother's Birth City" v-model="moduleState.application.birthRecords.motherBirthCity" trimmed/>
                        <FormSelectField label="Mother's Birth State" :options="ProfileTypes.State" v-model="moduleState.application.birthRecords.motherBirthState"/>
                        <FormTextField label="Mother's Birth County" v-model="moduleState.application.birthRecords.motherBirthCounty" trimmed/>
                    </Columns>
                </div>

                <div v-if="moduleState.application.socialSecurity">
                    <HalfColumn>
                        <FormTextField label="Mother's Social Security Number" v-model="moduleState.application.socialSecurity.motherSocialSecurityNumber" trimmed :length="9" isPassword noAutoFill />
                    </HalfColumn>
                </div>

                <h2 class="pageHeading mt-6">Please provide some information about your father</h2>

                <Columns>
                    <FormTextField label="Birth Father's Full Name" v-model="moduleState.application.fatherName" trimmed/>
                    <FormTextField label="Father's Surname Before Marriage" v-model="moduleState.application.fatherMaidenName" trimmed/>
                </Columns>

                <div v-if="moduleState.application.birthRecords">
                    <Columns>
                        <FormTextField label="Father's Birth City" v-model="moduleState.application.birthRecords.fatherBirthCity" trimmed/>
                        <FormSelectField label="Father's Birth State" :options="ProfileTypes.State" v-model="moduleState.application.birthRecords.fatherBirthState"/>
                        <FormTextField label="Father's Birth County" v-model="moduleState.application.birthRecords.fatherBirthCounty" trimmed/>
                    </Columns>
                </div>

                <div v-if="moduleState.application.socialSecurity">
                    <HalfColumn>
                        <FormTextField label="Father's Social Security Number" v-model="moduleState.application.socialSecurity.fatherSocialSecurityNumber" trimmed :length="9" isPassword noAutoFill />
                    </HalfColumn>
                </div>

                <div v-if="moduleState.application.birthRecords">
                    <h2 class="pageHeading mt-6">Please provide some information about your parents' marital status</h2>

                    <FormRadioGroupField label="Were Your Parents Married When You Were Born? *" :options="ProfileTypes.YesNoUnknown" v-model="moduleState.application.birthRecords.parentsMaritalStatus"/>
                </div>
            </div>

            <!-- Page: Adoption -->
            <div v-if="form.state.activePage == 'adoption'">
                <h2 class="pageHeading">Please provide some information about your adoption status</h2>

                <div v-if="moduleState.application.birthRecords">
                    <FormRadioGroupField label="Were You Adopted? *" :options="ProfileTypes.YesNoUnknown" v-model="moduleState.application.birthRecords.adoptionStatus"/>

                    <div v-if="moduleState.application.birthRecords.adoptionStatus == ProfileTypes.YesNoUnknown.Yes">
                        <Columns>
                            <FormTextField label="Your Full Name After Adoption" v-model="moduleState.application.birthRecords.adoptedName" trimmed autoFocus/>
                            <FormTextField label="Adopted Mother's Full Name" v-model="moduleState.application.birthRecords.adoptedMotherName" trimmed/>
                            <FormTextField label="Adopted Father's Full Name" v-model="moduleState.application.birthRecords.adoptedFatherName" trimmed/>
                        </Columns>

                        <FormRadioGroupField label="Do you want a birth certificate with your name prior to adoption (birth name), or after adoption? *"
                            :options="ProfileTypes.AdoptionRequestType" :excludedOptions="[ProfileTypes.AdoptionRequestType['N/A']]" v-model="moduleState.application.birthRecords.adoptionRequestType"/>
                    </div>
                </div>
            </div>

            <!-- Page: Siblings -->
            <div v-if="form.state.activePage == 'siblings'">
                <h2 class="pageHeading">Please provide some information about your siblings</h2>

                <div v-if="moduleState.application.birthRecords">
                    <ListEditor class="mt-3" header="Enter The Names Of Your Siblings *"
                        :items="moduleState.application.birthRecords.siblings"
                        itemIcon="fa-user" :itemLabel="(sibling)=>sibling.name"
                        addLabel="Add Sibling" :onAdd="()=>siblings.add()"
                        :onEdit="(idx)=>siblings.edit(idx)" :onRemove="(idx)=>siblings.remove(idx)" />

                        <ModalDialog
                            v-if="siblings.activeItem" active
                            title="Sibling" severity="link"
                            firstButtonText="Cancel" firstButtonSeverity="danger" @firstbuttonselected="siblings.activeItem = null"
                            secondButtonText="Save"  secondButtonSeverity="link"  @secondbuttonselected="siblings.onEditComplete()">

                            <Columns>
                                <FormTextField label="Full Name *" v-model="siblings.activeItem.name" trimmed autoFocus/>
                            </Columns>

                            <Columns style="margin-bottom: 12rem">
                                <FormDateField label="Date Of Birth *" v-model="siblings.activeItem.dateOfBirth" past/>
                                
                                <div class="column" style="padding-top: 2rem">
                                    <span v-if="siblings.activeItem.dateOfBirth">
                                        This sibling is <strong>{{ formatAge(siblings.activeItem.dateOfBirth) }}</strong> old
                                    </span>
                                </div>
                            </Columns>

                            <div v-if="siblings.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                                <p v-for="error in siblings.errors" :key="error">{{ error }}</p>
                            </div>
                            <div v-else class="mt-6"></div>

                        </ModalDialog>
                </div>
            </div>

            <!-- Page: Social Security mailing info -->
            <div v-if="form.state.activePage == 'mailing'">
                <h2 class="pageHeading">Please provide the location where your Social Security card should be mailed</h2>

                <div v-if="moduleState.application.socialSecurity">
                    <FormCheckboxField :boxLabel="`I have no fixed address - use ${sessionState.session!.user.assignedCareProvider!.name} address instead`" v-model="moduleState.application.socialSecurity.useCareProviderAddressForMailing"/>

                    <div v-if="!moduleState.application.socialSecurity.useCareProviderAddressForMailing">
                        <Columns>
                            <FormTextField label="Building Number / Name *" v-model="moduleState.application.socialSecurity.mailingAddress!.number" autoFocus />
                            <FormTextField label="Street *" v-model="moduleState.application.socialSecurity.mailingAddress!.street" trimmed />
                        </Columns>

                        <Columns>
                            <FormTextField label="City *" v-model="moduleState.application.socialSecurity.mailingAddress!.city" trimmed />
                            <FormTextField label="Zip Code *" v-model="moduleState.application.socialSecurity.mailingAddress!.postalCode" numeric :length="9" />
                            <FormSelectField label="State *" :options="ProfileTypes.StateNotUnknown" allowNullSelection v-model="moduleState.application.socialSecurity.mailingAddress!.state" />
                        </Columns>
                    </div>
                </div>
            </div>

            <!-- Page: Possessed ID types -->
            <div v-if="form.state.activePage == 'idType'">
                <h2 class="pageHeading">Do you have any existing ID documents which you can scan or photograph?</h2>

                <p>This will improve the chance of your application being successful.</p>
                <br/>
                <p>There are two categories of ID: <strong>Primary ID</strong> and <strong>Secondary ID</strong>.</p>
                <br/>

                <div class="infoBox">
                    <p class="mb-2"><strong>Primary ID -</strong> If you have any of these, you only need to supply <strong>one</strong>:</p>
                    <ul>
                        <li>Current state-issued driver's license</li>
                        <li>Current state-issued photo ID card</li>
                        <li>Current passport or visa</li>
                    </ul>
                </div>

                <br/>

                <div class="infoBox">
                    <p class="mb-2"><strong>Secondary ID -</strong> If you only have secondary ID, you will need to supply <strong>2-3</strong> of these:</p>
                    <ul>
                        <li>Social Security Card</li>
                        <li>Medicaid/Medicare Card</li>
                        <li>Current U.S. military ID</li>
                        <li>Current Department of Corrections photo ID card</li>
                        <li>Current student ID card</li>
                        <li>Veteran's ID</li>
                        <li>Voter ID</li>
                        <li>Temporary driver's license</li>
                        <li>Car registration or title with current address</li>
                        <li>Recent Bank statement with current address</li>
                        <li>Recent Pay stub with current address</li>
                        <li>Income tax return / W-2 form showing current address</li>
                        <li>Letter from government agency dated within the last six months, showing current address</li>
                    </ul>
                </div>

                <br/>

                <FormRadioGroupField label="Which type of ID do you have?" :options="IdTypeOptions" v-model="moduleState.miscState.idType.possessedIdType" stacked/>

                <div v-if="moduleState.miscState.idType.possessedIdType == 'NONE'" class="notification bulmaNotification is-warning mt-4">
                    <p>If you don't have any existing ID, we will still do our best to process your request.</p>
                    <br/>
                    <p>However, your chance of success is increased if you are able to provide some kind of documentation to support your application.</p>
                </div>
            </div>

            <!-- Page: ID upload -->
            <div v-if="form.state.activePage == 'supportDocUpload'">
                <h2 class="pageHeading">Please upload photos / scans of any existing ID documents you have available</h2>

                <p>When taking photos, <strong>please ensure the image is clear, and frame the photo tightly around your ID</strong></p>
                
                <div class="exampleImageRow mt-4">
                    <img class="exampleImage" src="../assets/images/example_id_invalid.png">
                    <img class="exampleImage" src="../assets/images/example_id_valid.png">
                </div>
                <div class="exampleImageRow mb-5">
                    <img class="exampleImage" src="../assets/images/example_document_invalid.png">
                    <img class="exampleImage" src="../assets/images/example_document_valid.png">
                </div>

                <p class="mb-3"><strong>Upload your photos below:</strong></p>
                <DocumentUploadWidget :documents="moduleState.supportDocuments" @upload="uploadSupportDocument($event)" @delete="deleteSupportDocument($event)"/>

                <div v-if="moduleState.formNavState.errors.length == 0 && moduleState.miscState.idType.possessedIdType == IdTypeOptions['Secondary ID'] && moduleState.supportDocuments.length < 2" class="notification bulmaNotification is-warning mt-6">
                    <p>If you only have secondary ID, you should upload 2 or more documents for the best chance of success in your application</p>
                </div>
            </div>

            <!-- Page: Survey questions -->
            <div v-if="form.state.activePage == 'votingIntent'">
                <h2 class="pageHeading">Your vote matters</h2>

                <div v-if="moduleState.application.birthRecords">
                    <FormRadioGroupField label="Do you intend to use your ID to vote? *" :options="YesNo" v-model="moduleState.application.birthRecords.votingIntent"/>
                </div>
            </div>

            <!-- Page: Equifax consent -->
            <div v-if="form.state.activePage == 'equifaxConsent'">
                <h2 class="pageHeading">Please let us know if you agree to us verifying your identity</h2>

                <p>Do we have your permission to verify your identity with Equifax?</p>
                <p>This will require us to share some of your information with them.</p>
                <br/>
                <p><strong>Verifying your identity may allow us to speed up your application, and improve the chance of a successful result.</strong></p>
                <br/>
                <p>However, you may opt-out if you'd prefer.</p>
                
                <FormRadioGroupField class="mt-3" label="Do you agree to us verifying your identity? *" :options="EquifaxConsentOptions" v-model="moduleState.miscState.equifaxConsent.consent"/>
            </div>

            <!-- Page: Notification preferences -->
            <div v-if="form.state.activePage == 'notificationPreferences'">
                <h2 class="pageHeading">Please let us know how we should keep you up-to-date about your application</h2>

                <FormCheckboxField boxLabel="By Email" v-model="moduleState.miscState.notificationPreferences.emailEnabled" :isDisabled="moduleState.miscState.notificationPreferences.noneEnabled"/>
                <HalfColumn v-if="moduleState.miscState.notificationPreferences.emailEnabled && !moduleState.miscState.notificationPreferences.noneEnabled">
                    <FormTextField label="Email Address" v-model="moduleState.notificationPreferences.email" trimmed autoFocus/>
                </HalfColumn>

                <FormCheckboxField boxLabel="By SMS" v-model="moduleState.miscState.notificationPreferences.smsEnabled" :isDisabled="moduleState.miscState.notificationPreferences.noneEnabled"/>
                <HalfColumn v-if="moduleState.miscState.notificationPreferences.smsEnabled && !moduleState.miscState.notificationPreferences.noneEnabled">
                    <FormTextField label="Phone Number" v-model="moduleState.notificationPreferences.sms" trimmed autoFocus/>
                </HalfColumn>

                <FormCheckboxField boxLabel="Do not contact me" v-model="moduleState.miscState.notificationPreferences.noneEnabled"/>

                <div v-if="moduleState.miscState.notificationPreferences.noneEnabled" class="notification bulmaNotification is-warning mt-4">
                    <p>If you don't provide any contact information, you will need to speak to your caseworker for updates on your application</p>
                </div>
            </div>

            <!-- Page: Pre-submit -->
            <div v-else-if="form.state.activePage == 'preSubmit'">
                <h2 class="pageHeading" style="text-align: center">Ok! We're ready to submit your application</h2>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <LoadingSpinner v-if="submissionInProgress" class="mt-4"/>
                    <SaveButton v-else large label="Submit!" icon="fa-paper-plane" @click="submitApplication()"/>
                </div>
            </div>

            <!-- Page: Completed confirmation -->
            <div v-else-if="form.state.activePage == 'completed'">
                <h1>Congratulations! Your application has been submitted</h1>
                <h2 class="pageHeading">You can return to the home page to view your applications.</h2>

                <div v-if="moduleState!.miscState.completed.notarizationRequired" class="notification bulmaNotification is-warning mt-4">
                    <p>A required form must be signed and attached to your application.</p>
                    <p>Please work with your caseworker now to complete this form to avoid processing delays.</p>
                </div>

                <div class="is-flex is-flex-direction-column is-align-items-center">
                    <SaveButton large label="Return To Home Page" @click="$router.push('/')"/>
                </div>
            </div>

            <!-- Footer / Navigation section -->
            <div v-if="form!.state.errors.length > 0" class="notification bulmaNotification is-danger mt-4">
                <p v-for="error in form.state.errors" :key="error">{{ error }}</p>
            </div>

            <div v-if="!submissionInProgress" class="is-flex is-flex-direction-row mt-6">
                <SaveButton v-if="form.canNavigateBack()" large label="Back" icon="fa-chevron-left" @click="form.navigateBack()"/>
                <div class="is-flex-grow-1"></div>
                <SaveButton v-if="form.canNavigateForward()" large label="Next" icon="fa-chevron-right" iconRight @click="form.navigateForward()"/>
            </div>    

        </div>
    </AppPageFrame>
</template>
  
<script lang="ts" setup>
import { 
    moduleState, initNewApplication, submitModule,
    initBirthRecordFormData, initSocialSecurityFormData,
    sections, type Page,
    districts, siblingStates, IdTypeOptions, EquifaxConsentOptions, YesNo
} from './module';

import { BirthRecords, Core, Feature, Identification, ProfileTypes, SocialSecurity } from '@minicity/core';
import { isModuleEnabled, sessionState } from '@/core/session';
import { uploadFile } from '@/core/api';

import LoadingSpinner from '@/components/LoadingSpinner.vue';
import LoadingPage from '@/components/LoadingPage.vue';
  
import { useForm, Form, PageSpecs } from '@/composables/useForm';
import { Validations } from '@/composables/useValidation';

import { computed, onMounted, onUnmounted, ref } from 'vue';
import { createDraftApplication, getDraftApplication, updateDraftApplicationContent } from '../api';
import { DeepPartial, formatAge, mapHttpError, notifyError } from '@/core/utils';
import { notifyNetworkError } from '@/core/utils';
import { getApplicationStatus } from '@/features/applicationStatus/api';

import { InteractionType, logFeatureInteraction } from '@/features/analytics/api';

import ListEditor from '@/components/ListEditor.vue';
import { useEditableList } from '@/composables/useEditableList';

const draftId = ref<string|null>(null);

/*
 * Define any special logic for each page
 */
const pages: PageSpecs<Page> = {
    "selectApplicationTypes": {
        validate: ()=>{
            const appl = moduleState.value!.application!;
            const enabledApplications = moduleState.value!.miscState.enabledApplications;

            if (!enabledApplications.birthRecords && !enabledApplications.socialSecurity) {
                return ["Please select which types of ID you wish to apply for"];
            }

            // Initialize application data
            // (and avoid destroying any existing data if the user had previously entered stuff and navigated back to this page)
            if (enabledApplications.birthRecords) {
                appl.birthRecords = appl.birthRecords || initBirthRecordFormData();
            } else {
                appl.birthRecords = undefined;
            }

            if (enabledApplications.socialSecurity) {
                appl.socialSecurity = appl.socialSecurity || initSocialSecurityFormData();
            } else {
                appl.socialSecurity = undefined;
            }
        },
        nextPage: "basicCitizenInfo",
    },
    "basicCitizenInfo": {
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (appl.firstName!.length == 0) return ["Please enter your first name"];
            if (appl.lastName!.length == 0) return ["Please enter your last name"];
            if (!appl.dateOfBirth) return ["Please select your date of birth"];

            if (appl.socialSecurity) {
                if (!appl.socialSecurity.sex) return ["Please select your sex / gender"];
            }
        },
        nextPage: "socialSecurity"
    },
    "socialSecurity": {
        enabled: () => moduleState.value?.application.socialSecurity != undefined,
        validate: ()=>{
            const appl = moduleState.value!.application!;

            if (!appl.socialSecurity!.previouslyAppliedForCard) return ["Please indicate whether you've previously applied for a Social Security card"];

            if (appl.socialSecurity!.previousSocialSecurityNumber!.length != 0 && appl.socialSecurity!.previousSocialSecurityNumber!.length != 9)
                return ["Social Security Number should be in the form AAABBCCCC. Leave blank if not known"];

            if (!appl.socialSecurity!.citizenship) return ["Please select your citizenship status"];

        },
        nextPage: "birthPlace"
    },
    "birthPlace": {
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (appl.birthCity!.length == 0) return ["Please enter the city where you were born"];
            if (appl.birthState == null) return ["Please select the state where you were born"];

            if (appl.birthRecords) {
                if (appl.birthRecords.birthHospital!.length == 0) return ["Please select the hospital where you were born"];
                
                if (districts[appl.birthState]) {
                    if (!appl.birthRecords.birthDistrict || !districts[appl.birthState]!.includes(appl.birthRecords.birthDistrict)) return ["Please select the district where you were born"];
                } else {
                    appl.birthRecords.birthDistrict = ProfileTypes.BirthDistrict['N/A'];
                }
            }
        },
        nextPage: "parents"
    },
    "parents": {
        validate: ()=>{
            const appl = moduleState.value!.application!;

            if (appl.fatherName?.length == 0) { appl.fatherName = "Unknown Unknown"; }
            if (appl.motherName?.length == 0) { appl.motherName = "Unknown Unknown"; }

            if (appl.birthRecords) {
                if (appl.fatherMaidenName?.length == 0) { appl.fatherMaidenName = "Unknown"; }
                if (appl.motherMaidenName?.length == 0) { appl.motherMaidenName = "Unknown"; }

                if (!appl.birthRecords.parentsMaritalStatus) return ["Please indicate whether you parents were married when you were born"];

                if (appl.socialSecurity) {
                    if (appl.socialSecurity!.motherSocialSecurityNumber!.length != 0 && appl.socialSecurity!.motherSocialSecurityNumber!.length != 9)
                        return ["Mother's Social Security Number should be in the form AAABBCCCC. Leave blank if not known"];

                    if (appl.socialSecurity!.fatherSocialSecurityNumber!.length != 0 && appl.socialSecurity!.fatherSocialSecurityNumber!.length != 9)
                        return ["Father's Social Security Number should be in the form AAABBCCCC. Leave blank if not known"];
                }
            }
        },
        nextPage: "adoption"
    },
    "adoption": {
        enabled: () => moduleState.value?.application.birthRecords != undefined,
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (!appl.birthRecords!.adoptionStatus) return ["Please indicate whether or not you were adopted"];

            if (appl.birthRecords!.adoptionStatus == ProfileTypes.YesNoUnknown.Yes) {
                if (!appl.birthRecords!.adoptionRequestType || appl.birthRecords!.adoptionRequestType == ProfileTypes.AdoptionRequestType['N/A'])
                    return ["Please indicate whether you want a certificate with your name prior to, or after adoption"];
            } else {
                appl.birthRecords!.adoptionRequestType = ProfileTypes.AdoptionRequestType['N/A'];
            }
        },
        nextPage: "siblings",
    },
    "siblings": {
        enabled: () => moduleState.value?.application.birthRecords != undefined && siblingStates.includes(moduleState.value.application.birthState!),
        onLoad: () => {
            if (!moduleState.value!.application.birthRecords!.siblings) {
                moduleState.value!.application.birthRecords!.siblings = [];
            }
        },
        nextPage: "mailing"
    },
    "mailing": {
        enabled: () => false, //moduleState.value?.application.socialSecurity != undefined,
        onLoad: () => {
            const appl = moduleState.value!.application!;
            if (!appl.socialSecurity!.mailingAddress) {
                appl.socialSecurity!.mailingAddress = {
                    number: "",
                    street: "",
                    city: appl.birthCity,
                    state: appl.birthState! as unknown as ProfileTypes.State,
                    postalCode: ""
                };
            }
        },
        validate: ()=>{
            const appl = moduleState.value!.application!;
            
            if (appl.socialSecurity!.useCareProviderAddressForMailing) {
                appl.socialSecurity!.mailingAddress = undefined;
            } else {
                if (appl.socialSecurity!.mailingAddress!.number!.length == 0) return ["Please enter your house / building number"];
                if (appl.socialSecurity!.mailingAddress!.street!.length == 0) return ["Please enter your street name"];
                if (appl.socialSecurity!.mailingAddress!.city!.length == 0) return ["Please enter your city"];
                if (appl.socialSecurity!.mailingAddress!.postalCode!.length == 0) return ["Please enter your zip code"];
                if (!appl.socialSecurity!.mailingAddress!.state) return ["Please select your state"];
            }
        },
        nextPage: "idType"
    },
    "idType": {
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (!moduleState.value!.miscState.idType.possessedIdType) return ["Please indicate which type of ID you have"];

            if (appl.birthRecords) {
                switch (moduleState.value!.miscState.idType.possessedIdType as IdTypeOptions) {
                    case IdTypeOptions['Primary ID']:          appl.birthRecords.possessedIdTypes = ProfileTypes.PossessedIdTypes.Primary;   break;
                    case IdTypeOptions['Secondary ID']:        appl.birthRecords.possessedIdTypes = ProfileTypes.PossessedIdTypes.Secondary; break;
                    case IdTypeOptions["I don't have any ID"]: appl.birthRecords.possessedIdTypes = ProfileTypes.PossessedIdTypes.None;      break;
                }
            }
        },
        nextPage: "supportDocUpload"
    },
    "supportDocUpload": {
        enabled: () => moduleState.value!.miscState.idType.possessedIdType != IdTypeOptions["I don't have any ID"],
        validate: ()=>{
            if (moduleState.value!.supportDocuments.length < 1) return ["Please upload at least one document. If you don't have any, hit 'Back'"];
        },
        nextPage: "votingIntent"
    },
    "votingIntent": {
        enabled: () => moduleState.value?.application.birthRecords != undefined,
        validate: ()=>{
            if (!moduleState.value!.application.birthRecords!.votingIntent)
                return ["Please indicate whether you intend to use your ID to vote in the next election"];
        },
        nextPage: "equifaxConsent"
    },
    "equifaxConsent": {
        enabled: () => isModuleEnabled(Feature.Type.EQUIFAX_CHECKS) && moduleState.value?.application.socialSecurity != undefined,
        onLoad: () => {
            const appl = moduleState.value!.application!;
            if (moduleState.value!.miscState.equifaxConsent.consent == null) {
                if (appl.socialSecurity?.equifaxConsent === true) {
                moduleState.value!.miscState.equifaxConsent.consent = EquifaxConsentOptions['Yes, my data can be shared'];
                } else if (appl.socialSecurity?.equifaxConsent === false) {
                    moduleState.value!.miscState.equifaxConsent.consent = EquifaxConsentOptions['No, do not share my data'];
                }
            }
        },
        validate: ()=>{
            const appl = moduleState.value!.application!;
            if (moduleState.value!.miscState.equifaxConsent.consent == null) return ["Please indicate whether you agree to us verifying your identity"];

            appl.socialSecurity!.equifaxConsent = moduleState.value!.miscState.equifaxConsent.consent == EquifaxConsentOptions['Yes, my data can be shared'];
        },
        nextPage: "notificationPreferences"
    },
    "notificationPreferences": {
        validate: ()=>{
            const prefs = moduleState.value!.notificationPreferences!;
            const pageState = moduleState.value!.miscState.notificationPreferences;

            if (pageState.noneEnabled) {
                prefs.email = undefined;
                prefs.sms = undefined;
            } else {
                if (!pageState.emailEnabled && !pageState.smsEnabled) return ["Please indicate how you would like to receive updates about your application"];

                if (pageState.emailEnabled) {
                    if (!Validations.email(prefs.email)) return ["Please enter a valid email address"];
                } else {
                    prefs.email = undefined;
                }

                if (pageState.smsEnabled) {
                    if (!Validations.phone_US(prefs.sms)) return ["Please enter a valid US phone number"];
                } else {
                    prefs.sms = undefined;
                }
            }
        },
        nextPage: "preSubmit"
    },
    "preSubmit": {},
    "completed": {disallowBack: true},
};


/*
 * Initialize
 */
let availableApplicationTypes: string[] = [];

 onMounted(() => {
    if (!sessionState.session) return;
    
    getApplicationStatus(sessionState.session.user.id)
    .then(applStatus => {
        // Determine which application types are available
        // The module must be enabled for the citizen's care provider, and they must not already have an application in-progress
        if (isModuleEnabled('BIRTH_RECORDS') && !applStatus.applications.find(appl => appl.applicationType == 'birthRecords')) {
            availableApplicationTypes.push('birthRecords');
        }

        if (isModuleEnabled('SOCIAL_SECURITY') && !applStatus.applications.find(appl => appl.applicationType == 'socialSecurity')) {
            if (!isModuleEnabled('ONLINE_SOCIAL_SECURITY_APPLICATION')) {
                availableApplicationTypes.push('socialSecurity');
            }
        }
        
        // Load or start a new application
        getDraftApplication(sessionState.session!.user.id, "identification")
        .then(draft => {
            console.log(`Resumed draft application ${draft.id}`);

            moduleState.value = JSON.parse(draft.content);
            draftId.value = draft.id;
            initFormNavigation();
        })
        .catch(err => {
            console.error("Failed to resume application", err);
            startNewApplication();
        });
    });
});

const startNewApplication = () => {
    console.log("Starting new application");

    moduleState.value = initNewApplication({
        // Initial misc state
        enabledApplications: {
            birthRecords: false,
            socialSecurity: false,
        },
        equifaxConsent: {
            consent: null,
        },
        idType: {
            possessedIdType: null
        },
        notificationPreferences: {
            emailEnabled: sessionState.session!.user.preferences.notifications.email != undefined,
            smsEnabled: sessionState.session!.user.preferences.notifications.sms != undefined,
            noneEnabled: false,
        },
        completed: {
            notarizationRequired: false,
        }
    });

    createDraftApplication(sessionState.session!.user.id, "identification", moduleState.value)
    .then(createdDraftId => {
        draftId.value = createdDraftId;
        initFormNavigation();
    })
    .catch(err => notifyError(err))
}

let formUpdateTimer: NodeJS.Timeout|null = null; 

const initFormNavigation = () => {
    form.value = useForm(pages, sections, moduleState.value!.formNavState, onFormChanged);

    // The set of available applications may have changed - detect and handle that by bouncing the user back to the application-type-selection screen
    let resetForm = false;
    if (moduleState.value!.application.birthRecords && !availableApplicationTypes.includes('birthRecords')) {
        moduleState.value!.application.birthRecords = undefined;
        moduleState.value!.miscState.enabledApplications.birthRecords = false;
        resetForm = true;
    }
    if (moduleState.value!.application.socialSecurity && !availableApplicationTypes.includes('socialSecurity')) {
        moduleState.value!.application.socialSecurity = undefined;
        moduleState.value!.miscState.enabledApplications.socialSecurity = false;
        resetForm = true;
    }

    if (resetForm) {
        console.log("Resetting form due to change in available application types");
        form.value.displayPage("selectApplicationTypes", true);
    }

    // Set a timer to regularly push updated form state to server (also keeps session alive)
    const updateTimer = () => {
        console.log("Update timer triggered");
        onFormChanged();
        formUpdateTimer = setTimeout(updateTimer, 30000);
    };
    formUpdateTimer = setTimeout(updateTimer, 30000);

    // Generate analytics event
    logFeatureInteraction("applications/identification", "IDENTIFICATION", InteractionType.CLICK, JSON.stringify({'draftId': draftId.value, 'page': form.value!.state.activePage, 'errors': form.value!.state.errors}));
}

onUnmounted(() => {
    if (formUpdateTimer) {
        console.log("Diabled update timer", formUpdateTimer);
        clearTimeout(formUpdateTimer);
    }
});

/*
 * Form state
 */
const form = ref<Form<Page>|null>(null);

const currentSection = computed(() => {
    if (!moduleState.value) return null;
    return form.value!.determineCurrentSection();
});

/*
 * List editors
 */
 const siblings = ref(useEditableList<ProfileTypes.Sibling>(
    () => moduleState.value!.application.birthRecords!.siblings! as DeepPartial<ProfileTypes.Sibling>[], // Items
    () => { return {} }, // Create new item

    // Validate
    (item: DeepPartial<ProfileTypes.Sibling>) => {
        if (item.name!.length == 0) return ["Please enter full name name"];
        if (!item.dateOfBirth) return ["Please enter date of birth"];
        return [];
    },

    // On change
    () => onFormChanged()
));


/*
 * Document uploads
 */
const deleteSupportDocument = (doc: Core.Document) => {
    moduleState.value!.supportDocuments = moduleState.value!.supportDocuments.filter(d => d.id != doc.id);
    onFormChanged();
};
  
const uploadSupportDocument = (uploadInfo: {file: File, onDone: ()=>any}) => {
    uploadFile(uploadInfo.file, ['IMAGE','PDF'])
    .then(doc => {
        moduleState.value!.supportDocuments.push(doc);
        moduleState.value!.formNavState.errors = [];
        onFormChanged();
    })
    .catch(err => notifyNetworkError("Failed to upload document", err))
    .finally(() => uploadInfo.onDone());
}


/*
 * Draft saving / form submission
 */
let lastPushedModuleStateJson = "";
const onFormChanged = () => {
    const moduleStateJson = JSON.stringify(moduleState.value);
    if (moduleStateJson == lastPushedModuleStateJson) return; // No change - no need to update

    console.log("Pushing updated form state to server", moduleState.value);

    updateDraftApplicationContent(sessionState.session!.user.id, draftId.value!, moduleState.value)
    .then(() => lastPushedModuleStateJson = moduleStateJson);

    // Generate analytics event
    logFeatureInteraction("applications/identification", "IDENTIFICATION", InteractionType.PROGRESS, JSON.stringify({'draftId': draftId.value, 'page': form.value!.state.activePage, 'errors': form.value!.state.errors}));
};

const submissionInProgress = ref<boolean>(false);

const submitApplication = async () => {
    form.value!.state.errors = [];
    console.log("Submitting", moduleState.value!.application);

    submissionInProgress.value = true;
    try {
        const result = await submitModule(draftId.value!);
        moduleState.value!.miscState.completed.notarizationRequired = result.notarizationRequired;

        form.value!.displayPage("completed");

        // Generate analytics event
        logFeatureInteraction("applications/identification/submit", "IDENTIFICATION", InteractionType.COMPLETION, JSON.stringify({'draftId': draftId.value, 'page': form.value!.state.activePage}));
    } catch (ex) {
        console.log(ex);
        form.value!.state.errors = [`Failed to submit - ${mapHttpError(ex)}`];

        // Generate analytics event
        logFeatureInteraction("applications/identification/submit", "IDENTIFICATION", InteractionType.ERROR, JSON.stringify({'draftId': draftId.value, 'errors': form.value!.state.errors}));
    } finally {
        submissionInProgress.value = false;
    }
};

</script>
  
<style scoped>
.formPage {
    max-width: 800px;
    margin: 0px auto;
    padding: 1rem;

    text-align: start;
}


.pageHeading {
    margin-top: 2rem;
    margin-bottom: 2rem;
    opacity: 0.7;
    font-size: 120%;
}

.infoBox {
    background-color: rgba(0,0,0,0.1);
    padding: 1rem;
    border-radius: 0.5rem;
}

ul {
    list-style: inside;
}

.exampleImageRow {
    text-align: center;
}

.exampleImage {
    margin: 0.75rem;
    width: min(200px, 40%);
}

.contentWrapper {
    max-width: min(100%, 700px);
}
</style>
